import { round } from '../../../shared/numberUtil'
import {
  Currency,
  ExecutedPayment,
  ExecutedPaymentType,
  Maybe,
  ContractChangeValidationListFragment,
  NationalContractLevelEnum,
  EmployeeLevelEnum,
  Change,
  GovernmentJobTitle,
  GovernmentJobTitleTinyDto,
} from '../../../types'
import { ContractChangePayload } from './types'
import {
  UndefinedContractChangeValuesForGivenMonth,
  getUndefinedContractChangeValuesForGivenMonth,
  getDateByMonthForValidationWithoutBackdating,
} from './validations/validationHelpers'

export const MULTI_VALUE_SEPARATOR = ' | '

export type SlimExecutedPayment = Pick<ExecutedPayment, 'amount' | 'referenceYear' | 'paymentDate' | 'type' | 'currency'>

export type FilteredExecutedPayments = {
  valuableHours: result
  valuableSalary: result
  carAllowance: result
  bonusPrepayment: result
}

type result = Maybe<{ amount: number; currency: Currency }[]>

const filterPaymentsByType = (payments: SlimExecutedPayment[], filterType: ExecutedPaymentType) =>
  payments.filter(({ type }) => type === filterType)

const reducePayment = (payments: SlimExecutedPayment[]) => {
  if (payments.length === 0) {
    return null
  }

  const groupByCurrencyMap = new Map<Currency, number[]>(payments.map(({ currency }) => [currency, []]))
  payments.forEach(({ amount, currency }) => groupByCurrencyMap.get(currency)!.push(amount))

  return Array.from(groupByCurrencyMap.entries()).map(([currency, amount]) => {
    return {
      amount: amount.reduce((sum: number, a: number) => sum + a, 0),
      currency: currency,
    }
  })
}

export const reduceExecutedPayments = (payments: SlimExecutedPayment[], year: number): FilteredExecutedPayments => {
  const paymentsByReferenceYear = payments.filter(({ referenceYear }) => referenceYear === year)

  const filteredValuableHours = filterPaymentsByType(paymentsByReferenceYear, ExecutedPaymentType.ValuableHours)
  const valuableHoursValue = reducePayment(filteredValuableHours)

  const filteredVariableSalaryHours = filterPaymentsByType(paymentsByReferenceYear, ExecutedPaymentType.VariableSalaryComponent)
  const valuableSalaryComponentValue = reducePayment(filteredVariableSalaryHours)

  const filteredCarAllowance = filterPaymentsByType(paymentsByReferenceYear, ExecutedPaymentType.CarAllowance)
  const carAllowanceValue = reducePayment(filteredCarAllowance)

  const filteredBonusPrepayment = filterPaymentsByType(paymentsByReferenceYear, ExecutedPaymentType.TargetBonusPrepayment)
  const bonusPrepaymentValue = reducePayment(filteredBonusPrepayment)

  return {
    valuableHours: valuableHoursValue,
    valuableSalary: valuableSalaryComponentValue,
    carAllowance: carAllowanceValue,
    bonusPrepayment: bonusPrepaymentValue,
  }
}

const getContractChangesByMonthWithoutBackdating = (
  contractChangeValidationList: ContractChangeValidationListFragment,
  year?: number,
  month?: number,
): UndefinedContractChangeValuesForGivenMonth => {
  if (month === undefined) {
    month = new Date().getUTCMonth()
  }

  const validFrom: Date = getDateByMonthForValidationWithoutBackdating(month, year)
  return getUndefinedContractChangeValuesForGivenMonth(validFrom, contractChangeValidationList)
}

export const getCurrentSalaryBonusLevelNCLAndGJTValueForSelectedMonth = (
  validFrom: { year?: number; month?: number },
  contractChangeValidationList: ContractChangeValidationListFragment,
): {
  salary: number
  bonus: number
  employeeLevel: EmployeeLevelEnum
  nationalContractLevel: NationalContractLevelEnum | undefined
  governmentJobTitle: GovernmentJobTitleTinyDto | undefined
} => {
  const { salary, bonus, employeeLevel, nationalContractLevel, governmentJobTitle } = getContractChangesByMonthWithoutBackdating(
    contractChangeValidationList,
    validFrom.year,
    validFrom.month,
  )
  return { salary, bonus, employeeLevel, nationalContractLevel, governmentJobTitle }
}

/* This is useful when we have a change onto Government Job Title
  // Since the <Option> component of antd accepts strings or numbers only, we need to flat the object we have into the JSON so to be a concatenation of data.
  // From :
  //        governmentJobTitle: { hiringOfficeCode: string
  //                              department: string
  //                              title: string
  //                             }
  // 
  // To:    governmentJobTitle: hiringOfficeCode-department-title
  */
export const transformChangeData = (
  tableData: Pick<Change, 'id' | 'status' | 'type' | 'validFrom' | 'changes' | 'updatedDate'>[],
): Pick<Change, 'id' | 'status' | 'type' | 'validFrom' | 'changes' | 'updatedDate'>[] => {
  const res: Pick<Change, 'id' | 'status' | 'type' | 'validFrom' | 'changes' | 'updatedDate'>[] = []
  tableData.forEach((change) => {
    const element: Pick<Change, 'id' | 'status' | 'type' | 'validFrom' | 'changes' | 'updatedDate'> = { ...change }
    const changePayload = JSON.parse(change.changes)
    if (!!changePayload.governmentJobTitle) {
      const newChangePayload: ContractChangePayload = {
        employeeLevel: changePayload.employeeLevel || undefined,
        salary: changePayload.salary || undefined,
        bonus: changePayload.bonus || undefined,
        nationalReferenceLevel: changePayload.nationalReferenceLevel || undefined,
        governmentJobTitle:
          changePayload.governmentJobTitle &&
          changePayload.governmentJobTitle.hiringOfficeCode &&
          changePayload.governmentJobTitle.department &&
          changePayload.governmentJobTitle.title
            ? changePayload.governmentJobTitle.hiringOfficeCode
                .concat(MULTI_VALUE_SEPARATOR)
                .concat(changePayload.governmentJobTitle.department)
                .concat(MULTI_VALUE_SEPARATOR)
                .concat(changePayload.governmentJobTitle.title)
            : undefined,
      }
      const newChangePayloadString: string = JSON.stringify(newChangePayload)
      element.changes = newChangePayloadString
    }
    res.push(element)
  })
  return res
}

// This function is useful when managing government job titles
// From "hiringOfficeCode-department-title" to { hiringOfficeCode: string, department: string, title: string }
// Be careful about the string structure, otherwise an "undefined" will be returned.
export const transformFromStringToGovernmentJobTitle = (from: string | undefined): GovernmentJobTitleTinyDto | undefined => {
  if (!from || from === '') {
    return undefined
  }
  const splitData: string[] = from.split(MULTI_VALUE_SEPARATOR)
  if (splitData.length !== 3) {
    return undefined
  }
  return {
    hiringOfficeCode: splitData[0],
    department: splitData[1],
    title: splitData[2],
  }
}

export const isGovernmentJobTitleValid = (
  validGovernmentJobTitles: GovernmentJobTitle[],
  governmentJobTitleChange: GovernmentJobTitleTinyDto | string,
): boolean => {
  if (typeof governmentJobTitleChange === 'string') {
    const transformedGovernmentJobTitle = transformFromStringToGovernmentJobTitle(governmentJobTitleChange)
    return transformedGovernmentJobTitle
      ? validGovernmentJobTitles.some(
          (el) =>
            el.hiringOffice.code === transformedGovernmentJobTitle.hiringOfficeCode &&
            el.department === transformedGovernmentJobTitle.department &&
            el.title === transformedGovernmentJobTitle.title,
        )
      : false
  } else {
    return validGovernmentJobTitles.some(
      (el) =>
        el.hiringOffice.code === governmentJobTitleChange.hiringOfficeCode &&
        el.department === governmentJobTitleChange.department &&
        el.title === governmentJobTitleChange.title,
    )
  }
}

export const getGovernmentJobTitle = (
  validGovernmentJobTitles: GovernmentJobTitle[],
  governmentJobTitleChange: GovernmentJobTitleTinyDto | string,
): GovernmentJobTitle | undefined => {
  if (typeof governmentJobTitleChange === 'string') {
    const transformedGovernmentJobTitle = transformFromStringToGovernmentJobTitle(governmentJobTitleChange)
    return transformedGovernmentJobTitle
      ? validGovernmentJobTitles.find(
          (el) =>
            el.hiringOffice.code === transformedGovernmentJobTitle.hiringOfficeCode &&
            el.department === transformedGovernmentJobTitle.department &&
            el.title === transformedGovernmentJobTitle.title,
        )
      : undefined
  } else {
    if (
      governmentJobTitleChange &&
      governmentJobTitleChange.hiringOfficeCode &&
      governmentJobTitleChange.department &&
      governmentJobTitleChange.title
    ) {
      return validGovernmentJobTitles.find(
        (el) =>
          el.hiringOffice.code === governmentJobTitleChange.hiringOfficeCode &&
          el.department === governmentJobTitleChange.department &&
          el.title === governmentJobTitleChange.title,
      )
    }
    return undefined
  }
}

export const isMonthlySalaryInRange = (monthlySalary: number, monthlySalaryMinimum: number, monthlySalaryMaximum: number): boolean => {
  return round(monthlySalary, 2) >= round(monthlySalaryMinimum, 2) && round(monthlySalary, 2) <= round(monthlySalaryMaximum, 2)
}
